import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';

const PRODUCTION_EPL_HOST = 'npcadmin.collegeboard.org';
export const EPL_DEV_MOCK_PREFIX = 'dev-mock:';

/** True when College Board IAM should be bypassed in favor of the dev mock UI. */
export function isEplMockEnvironment(): boolean {
    if (typeof window === 'undefined') {
        return true;
    }
    return window.location.hostname !== PRODUCTION_EPL_HOST;
}

export type EplMockScenario = 'schoolAccess' | 'noAccess';

export const EPL_MOCK_SCENARIOS: ReadonlyArray<{
    id: EplMockScenario;
    label: string;
    description: string;
}> = [
    {
        id: 'schoolAccess',
        label: 'School access',
        description: 'Known orgId 2354 — routes to school dashboard when provisioned.',
    },
    {
        id: 'noAccess',
        label: 'No access',
        description: 'Unknown orgId 99999 — routes to /no-access for permission request.',
    },
];

@Injectable({
    providedIn: 'root'
})
export class EplService {
    authService = inject(AuthService);
    redirectUrl = 'https://cbaccount.collegeboard.org/professional/enroll?prgCode=NPCAdmin&appId=488';
    serviceInitiated = false;

    loggedIn = new BehaviorSubject(false);
    cbUserEmail = new BehaviorSubject('');
    eplServiceInitialized = new BehaviorSubject(false);

    async initiateEpl(): Promise<void> {
        if (isEplMockEnvironment()) {
            return;
        }

        if (this.serviceInitiated) {
            return;
        }

        try {
            const iam = cb?.core?.iam;
            if (!iam) {
                throw new Error('College Board IAM is not available');
            }

            const iamBus = iam.getEventBus();
            iamBus.on(iam.events.AWSLogin, () => { void this.attemptLogin(); });
            iamBus.on(iam.events.Logout, () => { this.onLogout(); });
            iamBus.on(iam.events.AWSLoginFailed, () => { this.onLoginFailed(); });

            this.eplServiceInitialized.next(true);
        } catch {
            this.eplServiceInitialized.next(false);
            this.authService.eplLoginError$.next(
                'College Board sign-in could not be initialized. Please refresh and try again.'
            );
            return;
        }

        this.serviceInitiated = true;
        await this.prepareSessionAndLogin();
    }

    /** True when the user has returned from College Board with an auth token ready to exchange. */
    hasCollegeBoardAuthToken(): boolean {
        if (isEplMockEnvironment() || typeof window === 'undefined') {
            return false;
        }

        try {
            return !!this.getAuthorizationToken();
        } catch {
            return false;
        }
    }

    /** Starts EPL on first visit, or re-attempts login after the SDK is already initialized. */
    async proceedToCollegeBoardLogin(): Promise<void> {
        if (isEplMockEnvironment()) {
            return;
        }

        if (this.serviceInitiated) {
            await this.prepareSessionAndLogin();
            return;
        }

        await this.initiateEpl();
    }

    async mockLogin(scenario: EplMockScenario): Promise<void> {
        await this.authService.eplLogin(`${EPL_DEV_MOCK_PREFIX}${scenario}`);
    }

    /** Clears any existing Firebase session before exchanging the College Board token. */
    private async prepareSessionAndLogin(): Promise<void> {
        await this.authService.logout(false);
        await this.attemptLogin();
    }

    private async attemptLogin(): Promise<void> {
        const authorizationToken = this.getAuthorizationToken();
        if (!authorizationToken) {
            window.location.href = this.redirectUrl;
            return;
        }

        await this.authService.eplLogin(authorizationToken);
    }

    private onLogout() {
        this.loggedIn.next(false);
        this.cbUserEmail.next('');
        void this.authService.logout();
    }

    private onLoginFailed() {
        this.loggedIn.next(false);
        this.cbUserEmail.next('');
        this.authService.eplLoginError$.next(
            'College Board sign-in failed. Please try again.'
        );
    }

    private getAuthorizationToken(): string | undefined {
        const token = cb?.core?.iam?.getAuthorizationToken();
        return typeof token === 'string' && token.trim() ? token.trim() : undefined;
    }
}
